import React, { useState } from 'react';
import { useWeb } from '../context/WebContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { FaEye, FaSyncAlt } from 'react-icons/fa';

export default function Gallery() {
  const { gallery, settings } = useWeb();
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredItem, setHoveredItem] = useState(null);
  
  // States for toggleable before-after slides
  const [toggleBeforeAfter, setToggleBeforeAfter] = useState({});

  useDocumentMeta(settings.meta.gallery.title, settings.meta.gallery.desc);

  // Derive categories from gallery list
  const categories = ['All', ...new Set(gallery.map(item => item.category))];

  const filteredGallery = activeCategory === 'All' 
    ? gallery 
    : gallery.filter(item => item.category === activeCategory);

  const handleToggleState = (id) => {
    setToggleBeforeAfter(prev => ({
      ...prev,
      [id]: !prev[id] // toggles between true (after) and false (before)
    }));
  };

  return (
    <div className="bg-slate-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center space-y-3 mb-16">
          <span className="text-xs uppercase bg-orange-50 text-orange-600 font-extrabold px-3 py-1 rounded-full border border-orange-200">
            Installation Works Gallery
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Our Completed Projects
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm sm:text-base">
            Take a look at our recent installations. We provide premium quality net anchoring on high-rises.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2.5 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all border outline-none ${
                activeCategory === cat
                  ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                  : 'bg-white text-slate-650 hover:bg-slate-100 border-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8">
          {filteredGallery.map((item) => {
            const isBeforeAfter = item.isBeforeAfter;
            const showAfter = toggleBeforeAfter[item.id] ?? true; // defaults to after/main image

            return (
              <div 
                key={item.id} 
                className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-slate-200 group flex flex-col h-full"
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {/* Image Container */}
                <div className="relative h-36 sm:h-72 overflow-hidden bg-slate-900">
                  <img 
                    src={isBeforeAfter ? (showAfter ? item.afterImage : item.beforeImage) : item.image} 
                    alt={item.title} 
                    className="w-full h-full object-cover transition-transform duration-500"
                  />

                  {/* Before/After Overlay Flag */}
                  {isBeforeAfter && (
                    <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-orange-600 text-white text-[8px] sm:text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-md shadow flex items-center space-x-1 sm:space-x-1.5 z-10">
                      <span>{showAfter ? 'After' : 'Before'}</span>
                    </div>
                  )}

                  {/* Toggle Button for Before/After */}
                  {isBeforeAfter && (
                    <button
                      onClick={() => handleToggleState(item.id)}
                      className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-slate-900/90 hover:bg-slate-900 text-white backdrop-blur px-2 py-1 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl text-[9px] sm:text-xs font-semibold border border-slate-700 shadow-lg flex items-center space-x-1 sm:space-x-2 transition z-10 active:scale-95"
                    >
                      <FaSyncAlt className="text-[8px] sm:text-[10px] text-orange-500 animate-spin-slow" />
                      <span>
                        <span className="hidden sm:inline">Compare </span>
                        <span>({showAfter ? 'Before' : 'After'})</span>
                      </span>
                    </button>
                  )}

                  {/* Hover Overlay */}
                  {hoveredItem === item.id && !isBeforeAfter && (
                    <div className="absolute inset-0 bg-slate-950/40 flex items-center justify-center transition-all duration-300">
                      <div className="bg-white text-slate-900 p-2.5 rounded-full shadow-2xl scale-100 hover:scale-115 transition">
                        <FaEye />
                      </div>
                    </div>
                  )}
                </div>

                {/* Info Container */}
                <div className="p-3 sm:p-5 flex-grow flex flex-col space-y-0.5 sm:space-y-1">
                  <span className="text-[8px] sm:text-[10px] uppercase font-bold text-orange-600 tracking-wider">
                    {item.category}
                  </span>
                  <h3 className="text-xs sm:text-lg font-bold text-slate-900 leading-tight">
                    {item.title}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredGallery.length === 0 && (
          <div className="text-center py-16 bg-white border rounded-3xl p-8 max-w-md mx-auto space-y-3">
            <p className="text-slate-500 text-sm">No photos found in this category.</p>
          </div>
        )}

      </div>
    </div>
  );
}
